import { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const scene = {
  src: 'https://static.readdy.ai/image/7c1e788bcb66949f87d807c4f125e261/22eaa52b33142a92269b19424873fc46.png',
  alt: 'Dra. Nathália Posso caminhando com confiança em seu consultório',
};

// Generate random particles
function generateParticles(count: number) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const size = Math.random() * 3 + 1;
    const left = Math.random() * 100;
    const delay = Math.random() * 8;
    const duration = Math.random() * 6 + 6;
    const type = Math.random() > 0.5 ? 'floatParticle' : 'floatParticle2';
    particles.push({ id: i, size, left, delay, duration, type });
  }
  return particles;
}

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const taglineRef = useRef<HTMLParagraphElement>(null);
  const titleLine1Ref = useRef<HTMLSpanElement>(null);
  const titleLine2Ref = useRef<HTMLSpanElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const crmRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [particles] = useState(() => generateParticles(18));

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Overlay fade in
      if (overlayRef.current) {
        gsap.fromTo(
          overlayRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 1.5, ease: 'power2.out', delay: 0.2 }
        );
      }

      // Cinematic text cascade — Logo → Title → Text → Buttons
      const textEls = [
        taglineRef.current,
        titleLine1Ref.current,
        titleLine2Ref.current,
        subtitleRef.current,
        ctaRef.current,
        crmRef.current,
        statsRef.current,
        scrollRef.current,
      ].filter(Boolean);
      gsap.set(textEls, { opacity: 0, y: 40, scale: 0.97 });

      const tl = gsap.timeline({
        delay: 0.7,
        defaults: { ease: 'power3.out' },
      });

      tl.to(taglineRef.current,    { opacity: 1, y: 0, scale: 1, duration: 1.0 }, 0.0)
        .to(titleLine1Ref.current, { opacity: 1, y: 0, scale: 1, duration: 1.2 }, 0.3)
        .to(titleLine2Ref.current, { opacity: 1, y: 0, scale: 1, duration: 1.2 }, 0.55)
        .to(subtitleRef.current,   { opacity: 1, y: 0, scale: 1, duration: 1.0 }, 0.9)
        .to(ctaRef.current,        { opacity: 1, y: 0, scale: 1, duration: 0.9 }, 1.25)
        .to(crmRef.current,        { opacity: 1, y: 0, scale: 1, duration: 0.8 }, 1.55)
        .to(statsRef.current,      { opacity: 1, y: 0, scale: 1, duration: 0.7 }, 1.75)
        .to(scrollRef.current,      { opacity: 1, y: 0, scale: 1, duration: 0.7 }, 1.95);

      // Content fades out as user scrolls past Hero
      if (contentRef.current) {
        gsap.to(contentRef.current, {
          opacity: 0,
          y: 50,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: '55% top',
            end: '90% top',
            scrub: 1,
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative w-full"
      style={{ height: '120vh' }}
    >
      <div
        ref={stickyRef}
        className="sticky top-0 left-0 w-full h-[100dvh] min-h-[600px] overflow-hidden bg-[#1a1a1a]"
      >
        {/* Single full-screen scene image — static */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src={scene.src}
            alt={scene.alt}
            title="Dra. Nathália Posso | Ginecologista em Fortaleza"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Subtle warm gradient wash — light amber toning */}
        <div
          className="absolute inset-0 pointer-events-none z-[3]"
          style={{
            background: 'linear-gradient(170deg, rgba(195,155,75,0.10) 0%, transparent 30%, transparent 70%, rgba(170,125,60,0.08) 100%)',
          }}
        />

        {/* Light particles — floating golden dust */}
        <div className="absolute inset-0 pointer-events-none z-[3] overflow-hidden">
          {particles.map((p) => (
            <div
              key={p.id}
              className="particle"
              style={{
                width: `${p.size}px`,
                height: `${p.size}px`,
                left: `${p.left}%`,
                bottom: '-5%',
                animation: `${p.type} ${p.duration}s ${p.delay}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>

        {/* Cinematic dark overlay */}
        <div
          ref={overlayRef}
          className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/65 pointer-events-none z-[4]"
        />

        {/* Vignette */}
        <div
          className="absolute inset-0 pointer-events-none z-[4]"
          style={{
            background:
              'radial-gradient(ellipse at 50% 38%, transparent 25%, rgba(0,0,0,0.55) 100%)',
          }}
        />

        {/* Floating text content */}
        <div
          ref={contentRef}
          className="absolute inset-0 z-10 flex flex-col items-center justify-end pb-36 md:pb-28 px-6 md:px-12 text-center"
          style={{ willChange: 'transform, opacity' }}
        >
          <p
            ref={taglineRef}
            className="font-body text-[8px] md:text-[11px] text-white/50 tracking-[0.3em] md:tracking-[0.5em] uppercase mb-4 md:mb-8 whitespace-nowrap"
          >
            MENOPAUSA · Saúde ÍNTIMA · GINECOLOGIA ESTÉTICA
          </p>

          <h1
            className="font-heading text-white font-light leading-[1.12] tracking-tight max-w-4xl"
            style={{ fontSize: 'clamp(1.9rem, 5.2vw, 4.4rem)' }}
          >
            <span ref={titleLine1Ref} className="block">
              Sua saúde íntima merece{' '}
              <span className="italic font-normal">cuidado</span>,
            </span>
            <span ref={titleLine2Ref} className="block mt-1 md:mt-2">
              <span className="italic font-normal">acolhimento</span> e{' '}
              <span className="italic font-normal">confiança</span>.
            </span>
          </h1>

          <p
            ref={subtitleRef}
            className="font-body text-sm md:text-base text-white/55 leading-relaxed max-w-lg mt-6 md:mt-8 font-light"
          >
            Atendimento ginecológico humanizado para mulheres que buscam
            excelência, conforto e respeito em cada etapa.
          </p>

          <div ref={ctaRef} className="flex flex-col sm:flex-row gap-3 md:gap-4 mt-6 md:mt-10">
            <a
              href="https://api.whatsapp.com/send/?phone=5585984293412&text=Ol%C3%A1%21+Gostaria+de+informa%C3%A7%C3%B5es+sobre+procedimentos+e+consultas+com+a+Dra+Nathalia&type=phone_number&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-shimmer font-body text-xs font-semibold text-[#1a1a1a] bg-white hover:bg-white/90 hover:scale-[1.03] px-8 md:px-10 py-3 md:py-3.5 tracking-[0.12em] uppercase transition-all duration-500 whitespace-nowrap"
            >
              Agendar Consulta
            </a>
            <a
              href="https://www.google.com/maps/place/Femme+Vie/@-3.7414551,-38.4872636,17z/data=!3m1!4b1!4m6!3m5!1s0x7c748ee49695555:0x5ae6d9fed6392be3!8m2!3d-3.7414551!4d-38.4872636!16s%2Fg%2F11fzk6bkdp?entry=ttu&g_ep=EgoyMDI2MDgwNS4xIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-xs font-medium text-white hover:text-white hover:scale-[1.03] px-8 md:px-10 py-3 md:py-3.5 tracking-[0.12em] uppercase transition-all duration-300 border border-white/25 hover:border-white/55 whitespace-nowrap bg-white/5 backdrop-blur-sm"
            >
              COMO CHEGAR À FEMME VIE
            </a>
          </div>

          <div ref={crmRef} className="flex items-center gap-8 mt-5 md:mt-10">
            <div className="flex flex-col items-center">
              <span className="font-body text-[9px] md:text-[10px] text-white/35 tracking-[0.2em] md:tracking-[0.25em] uppercase">
                CRM
              </span>
              <span className="font-body text-xs text-white/60 mt-0.5">14200</span>
            </div>
            <div className="w-[1px] h-8 bg-white/12" />
            <div className="flex flex-col items-center">
              <span className="font-body text-[9px] md:text-[10px] text-white/35 tracking-[0.2em] md:tracking-[0.25em] uppercase">
                RQE
              </span>
              <span className="font-body text-xs text-white/60 mt-0.5">8571</span>
            </div>
          </div>

          {/* Discreet stats — between CRM and scroll */}
          <div
            ref={statsRef}
            className="flex items-center gap-4 md:gap-8 mt-4 md:mt-8"
          >
            <div className="flex items-baseline gap-1">
              <span className="font-heading text-base md:text-xl text-white/50 font-light">+2.800</span>
              <span className="font-body text-[8px] md:text-[9px] text-white/25 tracking-[0.1em] md:tracking-[0.15em] uppercase">Pacientes</span>
            </div>
            <div className="w-[1px] h-3 md:h-4 bg-white/10" />
            <div className="flex items-baseline gap-1">
              <span className="font-heading text-base md:text-xl text-white/50 font-light">+12</span>
              <span className="font-body text-[8px] md:text-[9px] text-white/25 tracking-[0.1em] md:tracking-[0.15em] uppercase">Anos de experiência</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          ref={scrollRef}
          className="absolute bottom-2 md:bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 md:gap-2 z-10"
        >
          <span className="font-body text-[8px] md:text-xs tracking-[0.2em] md:tracking-[0.25em] text-white/55 uppercase whitespace-nowrap">
            ROLE PARA DESCOBRIR
          </span>
          <div className="w-[1px] h-10 bg-white/20 overflow-hidden relative">
            <div
              className="absolute top-0 left-0 w-full h-4 bg-white/60"
              style={{ animation: 'scrollPulse 2.2s ease-in-out infinite' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}